import { ChevronDown, ChevronRight, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { ProductDualListItem } from './ProductDualListSelectorRhf'

const normalizeSearch = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')

interface ProductDualListPanelProps {
  productItems: ProductDualListItem[]
  categoryId?: number
  categoryName?: string
  value: number[]
  onChange: (ids: number[]) => void
  disabled?: boolean
}

export const ProductDualListPanel = ({
  productItems,
  categoryId,
  categoryName,
  value,
  onChange,
  disabled,
}: ProductDualListPanelProps) => {
  const [search, setSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const selectedSet = useMemo(() => new Set(value), [value])

  const conflictingProducts = useMemo(
    () =>
      productItems.filter(
        (item) => selectedSet.has(item.id) && item.groupId !== 0 && item.groupId !== categoryId,
      ),
    [selectedSet, productItems, categoryId],
  )

  const availableGroups = useMemo(() => {
    const normalizedSearch = normalizeSearch(search.trim())
    const filtered = productItems.filter((item) => {
      if (selectedSet.has(item.id)) return false
      if (!normalizedSearch) return true
      return normalizeSearch(item.label).includes(normalizedSearch)
    })
    const map = new Map<string, { key: string; label: string; items: ProductDualListItem[] }>()
    filtered.forEach((item) => {
      const key = String(item.groupId)
      if (!map.has(key)) map.set(key, { key, label: item.groupLabel, items: [] })
      map.get(key)!.items.push(item)
    })
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }),
    )
  }, [productItems, selectedSet, search])

  const selectedItems = useMemo(
    () => productItems.filter((item) => selectedSet.has(item.id)),
    [productItems, selectedSet],
  )

  const addProduct = (productId: number) => {
    if (selectedSet.has(productId)) return
    onChange([...value, productId])
  }

  const removeProduct = (productId: number) => {
    onChange(value.filter((id) => id !== productId))
  }

  const addGroup = (items: ProductDualListItem[]) => {
    const toAdd = items.filter((item) => !selectedSet.has(item.id)).map((item) => item.id)
    if (toAdd.length > 0) onChange([...value, ...toAdd])
  }

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Left panel: available products */}
        <div className="border border-[#e9eaeb] rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f9f9f7] px-4 py-3 border-b border-[#e9eaeb]">
            <p className="text-[12px] text-[#6a7282] uppercase tracking-wider">
              Produits disponibles
            </p>
          </div>
          <div className="p-3 flex flex-col">
            <div className="flex items-center border border-[#e9eaeb] rounded-lg px-3 py-2 mb-3">
              <Search size={14} className="text-[#717680] mr-2 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={disabled}
                placeholder="Rechercher un produit…"
                className="flex-1 text-[13px] outline-none bg-transparent disabled:cursor-not-allowed"
              />
            </div>
            <div className="space-y-1 max-h-75 overflow-y-auto">
              {availableGroups.length === 0 && (
                <p className="text-[13px] text-[#9ca3af] text-center py-8">
                  Aucun produit disponible
                </p>
              )}
              {availableGroups.map((group) => {
                const isOpen = expandedGroups.has(group.key)
                const allAdded = group.items.every((item) => selectedSet.has(item.id))
                return (
                  <div key={group.key}>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        className="flex-1 flex items-center justify-between px-2 py-2 text-[13px] text-[#181d27] hover:bg-[#f5f5f3] rounded-lg"
                      >
                        <span>{group.label}</span>
                        {isOpen ? (
                          <ChevronDown size={12} className="text-[#6a7282]" />
                        ) : (
                          <ChevronRight size={12} className="text-[#6a7282]" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => addGroup(group.items)}
                        disabled={allAdded || disabled}
                        title={`Ajouter tous les produits de ${group.label}`}
                        className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors shrink-0 ${
                          allAdded || disabled
                            ? 'text-[#c5c5c5] cursor-not-allowed'
                            : 'text-[#6a9560] hover:bg-[#f0fdf4] cursor-pointer'
                        }`}
                      >
                        <Plus size={16} strokeWidth={2} />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="ml-3 space-y-1">
                        {group.items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => addProduct(item.id)}
                            disabled={disabled}
                            className="w-full flex items-center justify-between px-2 py-1.5 text-[13px] text-[#374151] rounded-lg hover:bg-[#f0fdf4] group disabled:cursor-not-allowed"
                          >
                            <span>{item.label}</span>
                            <Plus
                              size={14}
                              className="text-[#6a9560] opacity-0 group-hover:opacity-100"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Right panel: selection */}
        <div className="border border-[#e9eaeb] rounded-xl overflow-hidden flex flex-col">
          <div className="bg-[#f0fdf4] px-4 py-3 border-b border-[#e9eaeb]">
            <p className="text-[12px] text-[#6a9560] uppercase tracking-wider">Sélection</p>
            {categoryName && <p className="text-[11px] text-[#6a7282] mt-0.5">{categoryName}</p>}
          </div>
          <div className="p-3">
            {selectedItems.length === 0 ? (
              <p className="text-[13px] text-[#9ca3af] text-center py-8">
                Aucun produit sélectionné
              </p>
            ) : (
              <div className="space-y-1 max-h-75 overflow-y-auto">
                {selectedItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between px-2 py-1.5 text-[13px] text-[#374151] bg-[#f9f9f7] rounded-lg"
                  >
                    <span>{item.label}</span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeProduct(item.id)}
                        className="text-[#ef4444] hover:text-[#dc2626]"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conflicting products warning */}
      {conflictingProducts.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm">
          <p className="font-medium text-orange-600 mb-2">
            Attention : les produits ci-dessous sont déjà dans une autre catégorie et seront
            déplacés.
          </p>
          <ul className="space-y-1">
            {conflictingProducts.map((product) => (
              <li key={product.id} className="text-orange-500 pl-2">
                {product.label} — {product.groupLabel}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
